import React, { useState, useRef } from "react";
import { Modal } from "reactstrap";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "react-toastify";
import { AuthService } from "../../services/AuthService";

interface GuardianConsentModalProps {
  isOpen: boolean;
  toggle: () => void;
}

interface GuardianConsentData {
  clientName: string;
  clientDateOfBirth: string;
  clientPrimaryInsuranceNumber?: string;
  clientSecondaryInsuranceNumber?: string;
  guardianName: string;
  guardianEmailAddress: string;
  guardianPhoneNumber: string;
  guardianSignatureBase64?: string;
  guardianSignatureDate?: string;
}

const GuardianConsentModal: React.FC<GuardianConsentModalProps> = ({ isOpen, toggle }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form fields
  const [clientName, setClientName] = useState("");
  const [clientDateOfBirth, setClientDateOfBirth] = useState("");
  const [clientPrimaryInsurance, setClientPrimaryInsurance] = useState("");
  const [clientSecondaryInsurance, setClientSecondaryInsurance] = useState("");
  const [guardianName, setGuardianName] = useState("");
  const [guardianEmail, setGuardianEmail] = useState("");
  const [guardianPhone, setGuardianPhone] = useState("");
  
  // Signature states
  const [guardianSignature, setGuardianSignature] = useState<string>("");
  const guardianSignatureRef = useRef<SignatureCanvas>(null);

  const saveGuardianSignature = () => {
    if (guardianSignatureRef.current) {
      const signatureData = guardianSignatureRef.current.toDataURL();
      setGuardianSignature(signatureData);
    }
  };

  const clearGuardianSignature = () => {
    if (guardianSignatureRef.current) {
      guardianSignatureRef.current.clear();
      setGuardianSignature("");
    }
  };



  const submitGuardianConsentForm = async () => {
    // Validation
    if (!clientName.trim()) {
      toast.error("Client Name is required", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });
      return;
    }

    if (!clientDateOfBirth.trim()) {
      toast.error("Client Date of Birth is required", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });
      return;
    }

    if (!guardianName.trim()) {
      toast.error("Guardian Name is required", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });
      return;
    }

    if (!guardianEmail.trim()) {
      toast.error("Guardian Email is required", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });
      return;
    }

    if (!guardianPhone.trim()) {
      toast.error("Guardian Phone is required", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });
      return;
    }

    if (!guardianSignature) {
      toast.error("Guardian signature is required", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const currentDate = new Date().toISOString();

      const guardianConsentData: GuardianConsentData = {
        clientName: clientName.trim(),
        clientDateOfBirth: clientDateOfBirth.trim(),
        clientPrimaryInsuranceNumber: clientPrimaryInsurance.trim() || undefined,
        clientSecondaryInsuranceNumber: clientSecondaryInsurance.trim() || undefined,
        guardianName: guardianName.trim(),
        guardianEmailAddress: guardianEmail.trim(),
        guardianPhoneNumber: guardianPhone.trim(),
        guardianSignatureBase64: guardianSignature,
        guardianSignatureDate: currentDate
      };

      console.log("Submitting guardian consent data:", guardianConsentData);

      // Call API through service (you'll need to add this method to AuthService)
      const result = await AuthService.createGuardianConsentData(guardianConsentData);
      console.log("Guardian consent submission result:", result);

      if (!result.success) {
        throw new Error(result.message || "Failed to submit guardian consent form");
      }

      toast.success("Guardian consent form submitted successfully!", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });

      toggle();
    } catch (error: any) {
      console.error("Error submitting guardian consent:", error);
      toast.error(error.message || "Failed to submit guardian consent form", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="guardian-consent-modal">
      <div className="modal-header d-flex justify-content-between align-items-center">
        <h5 className="modal-title">Legal Guardian Consent for Individual Counseling</h5>
        <button type="button" className="btn-close" onClick={toggle}></button>
      </div>
      
      <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {/* Form Fields */}
        <div className="mb-4">
          <div className="mb-3">
            <label className="form-label">Client Name: <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Enter client name"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Client Date of Birth: <span className="text-danger">*</span></label>
            <input
              type="date"
              className="form-control"
              value={clientDateOfBirth}
              onChange={(e) => setClientDateOfBirth(e.target.value)}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Client Primary Insurance Number:</label>
            <input
              type="text"
              className="form-control"
              value={clientPrimaryInsurance}
              onChange={(e) => setClientPrimaryInsurance(e.target.value)}
              placeholder="Enter primary insurance number"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Client Secondary Insurance Number:</label>
            <input
              type="text"
              className="form-control"
              value={clientSecondaryInsurance}
              onChange={(e) => setClientSecondaryInsurance(e.target.value)}
              placeholder="Enter secondary insurance number"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Guardian Name: <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              value={guardianName}
              onChange={(e) => setGuardianName(e.target.value)}
              placeholder="Enter guardian name"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Guardian Email Address: <span className="text-danger">*</span></label>
            <input
              type="email"
              className="form-control"
              value={guardianEmail}
              onChange={(e) => setGuardianEmail(e.target.value)}
              placeholder="Enter guardian email"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Guardian Phone Number: <span className="text-danger">*</span></label>
            <input
              type="tel"
              className="form-control"
              value={guardianPhone}
              onChange={(e) => setGuardianPhone(e.target.value)}
              placeholder="Enter guardian phone"
            />
          </div>
        </div>

        {/* Consent Content */}
        <div className="mb-4">
          <h6 className="mb-3">LEGAL GUARDIAN CONSENT FOR INDIVIDUAL COUNSELING</h6>
          
          <p>
            <strong>Lavni Incorporated</strong> provides counseling services to individuals in need of social/emotional/behavioral support.
            As the legal guardian with authority to consent on behalf of the individual named above, I hereby give my
            consent to the individual to receive individual counseling on a voluntary basis. I understand that I can
            withdraw my consent at any time. I understand that this consent form remains valid until individual counseling
            is deemed no longer necessary.
          </p>

          <p>
            <strong>Limits of Confidentiality:</strong> Lavni clinicians are professionally and legally mandated to report suspected abuse or
            if an individual is determined to be at risk of serious bodily harm to self or others. You will be advised of any
            planned reporting.
          </p>

          <p>
            If there are any questions, please contact Lavni's Clinical Director, Crystal Bush, LCSW LCAS LISW-CP, at{" "}
            <a href="mailto:crystal@mylavni.com" style={{ textDecoration: "none" }}>
              crystal@mylavni.com
            </a>, Subject: Legal Guardian.
          </p>
        </div>

        {/* Signature Section */}
        <div id="guardian-signature-section" className="mb-4">
          {/* <h6 className="mb-3">Guardian Signature</h6> */}
          
          <div className="mb-3">
            <label className="form-label">Guardian Signature: <span className="text-danger">*</span></label>
            <div style={{ border: "1px solid #ccc", borderRadius: "4px" }}>
              <SignatureCanvas
                ref={guardianSignatureRef}
                canvasProps={{
                  width: 500,
                  height: 150,
                  className: "signature-canvas"
                }}
                onEnd={saveGuardianSignature}
              />
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mt-2"
              onClick={clearGuardianSignature}
            >
              Clear Signature
            </button>
            <div className="mt-2">
              <small>Date: {new Date().toLocaleDateString()}</small>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="d-flex justify-content-center">
          <button
            type="button"
            className="btn btn-success"
            onClick={submitGuardianConsentForm}
            disabled={
              isSubmitting ||
              !clientName.trim() ||
              !clientDateOfBirth.trim() ||
              !guardianName.trim() ||
              !guardianEmail.trim() ||
              !guardianPhone.trim() ||
              !guardianSignature
            }
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Submitting...
              </>
            ) : (
              "Submit Guardian Consent Form"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default GuardianConsentModal;
